import React, { FC } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    FormControl,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import WeekNavigation from 'components/production-dashboard/overview/week-navigation.component';
import { getClinicsForLabDropdownTemplate } from 'apollo/templates/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Organization } from 'models/organization';
import { ProductionOverviewCountMode, ProductionOverviewPeriod } from 'models/production-overview';
import { getDatePickerFormat, getDateFnsLocale } from 'utils/format';
import {
    CustomRangeValidationError,
    addDays,
    getDefaultStartDate,
    getPeriodDayCount,
} from './production-overview-utils';

export type OverviewFilterState = {
    period: ProductionOverviewPeriod;
    startDate: Date;
    customFromDate: Date | null;
    customToDate: Date | null;
    clinic: Organization | null;
    countMode: ProductionOverviewCountMode;
};

type OverviewFilterBarProps = {
    filters: OverviewFilterState;
    onFiltersChange: (filters: OverviewFilterState) => void;
    rangeValidationError: CustomRangeValidationError;
    onPrint: () => void;
    isPrintDisabled: boolean;
};

// The page's own toggles follow the button rule: no uppercase, small like the inputs beside them.
const toggleButtonSx = { textTransform: 'none', paddingX: 1.5 } as const;

const OverviewFilterBar: FC<OverviewFilterBarProps> = ({
    filters,
    onFiltersChange,
    rangeValidationError,
    onPrint,
    isPrintDisabled,
}: OverviewFilterBarProps) => {
    const { t: translate } = useTranslation(['production-overview', 'common', 'orders', 'production-dashboard']);
    const { showBoundary } = useDenthubErrorBoundary();

    const { data: { clinicsForLab } = {}, loading: isClinicListLoading } = useQuery(getClinicsForLabDropdownTemplate, {
        variables: { filters: { showParents: true } },
        onError(error) {
            showBoundary(error);
        },
    });

    const handlePeriodChange = (_: React.MouseEvent, period: ProductionOverviewPeriod | null) => {
        if (period && period !== filters.period) onFiltersChange({ ...filters, period });
    };

    const handleCountModeChange = (_: React.MouseEvent, countMode: ProductionOverviewCountMode | null) => {
        if (countMode && countMode !== filters.countMode) onFiltersChange({ ...filters, countMode });
    };

    const handleClinicChange = (_: React.SyntheticEvent, clinic: Organization | null) => {
        onFiltersChange({ ...filters, clinic });
    };

    const isCustomPeriod = filters.period === ProductionOverviewPeriod.CUSTOM;

    // Like Production planning, the arrows move a whole period and Today goes back to the default start.
    const movePeriod = (direction: 1 | -1) =>
        onFiltersChange({
            ...filters,
            startDate: addDays(filters.startDate, direction * getPeriodDayCount(filters.period)),
        });

    return (
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={2} mb={1}>
            <ToggleButtonGroup
                size="small"
                color="primary"
                value={filters.countMode}
                exclusive
                onChange={handleCountModeChange}
                aria-label={translate('production-overview:SHOW_COUNT')}
            >
                <ToggleButton value={ProductionOverviewCountMode.ORDERS} sx={toggleButtonSx}>
                    {translate('production-overview:COUNT_MODE_ORDERS')}
                </ToggleButton>
                <ToggleButton value={ProductionOverviewCountMode.ACTIONS} sx={toggleButtonSx}>
                    {translate('production-overview:COUNT_MODE_ACTIONS')}
                </ToggleButton>
            </ToggleButtonGroup>

            <Autocomplete
                sx={{ minWidth: 364 }}
                loading={isClinicListLoading}
                id="production-overview-clinic-dropdown"
                options={clinicsForLab || []}
                onChange={handleClinicChange}
                value={(filters.clinic || { name: '' }) as Organization}
                getOptionLabel={(option: Organization) => option?.name || ''}
                renderOption={(props, option: Organization) => (
                    <li {...{ ...props, key: option?._id }}>
                        {option.name} &nbsp;
                        {!option.parentClinicId && !option.local && option.isMain ? (
                            <i> ({translate('common:MAIN')})</i>
                        ) : undefined}
                    </li>
                )}
                isOptionEqualToValue={(option: Organization, value: Organization) => option?._id === value?._id}
                renderInput={(params) => <TextField {...params} label={translate('common:CLINIC')} fullWidth={true} />}
            />

            <ToggleButtonGroup
                size="small"
                color="primary"
                value={filters.period}
                exclusive
                onChange={handlePeriodChange}
                aria-label={translate('production-overview:PERIOD')}
            >
                <ToggleButton value={ProductionOverviewPeriod.FOURTEEN_DAYS} sx={toggleButtonSx}>
                    {translate('production-overview:PERIOD_14_DAYS')}
                </ToggleButton>
                <ToggleButton value={ProductionOverviewPeriod.THIRTY_DAYS} sx={toggleButtonSx}>
                    {translate('production-overview:PERIOD_30_DAYS')}
                </ToggleButton>
                <ToggleButton value={ProductionOverviewPeriod.CUSTOM} sx={toggleButtonSx}>
                    {translate('production-overview:PERIOD_CUSTOM')}
                </ToggleButton>
            </ToggleButtonGroup>

            {!isCustomPeriod ? (
                <WeekNavigation
                    onPrev={() => movePeriod(-1)}
                    onNext={() => movePeriod(1)}
                    onToday={() => onFiltersChange({ ...filters, startDate: getDefaultStartDate() })}
                    isPrevDisabled={false}
                    disableGutter
                />
            ) : (
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
                    <Box display="flex" gap={1} alignItems="center">
                        <FormControl margin="dense">
                            <DatePicker
                                format={getDatePickerFormat()}
                                value={filters.customFromDate}
                                label={translate('production-overview:CUSTOM_RANGE_FROM')}
                                onChange={(date) => onFiltersChange({ ...filters, customFromDate: date })}
                            />
                        </FormControl>
                        <FormControl margin="dense">
                            <DatePicker
                                format={getDatePickerFormat()}
                                value={filters.customToDate}
                                label={translate('production-overview:CUSTOM_RANGE_TO')}
                                onChange={(date) => onFiltersChange({ ...filters, customToDate: date })}
                            />
                        </FormControl>
                    </Box>
                </LocalizationProvider>
            )}

            <Box flexGrow={1} />
            <Button
                startIcon={<PrintIcon />}
                onClick={onPrint}
                disabled={isPrintDisabled}
                data-test="production-overview.print-button"
            >
                {translate('orders:PRINT')}
            </Button>

            {isCustomPeriod && rangeValidationError ? (
                <Typography
                    variant="body2"
                    color="error"
                    width="100%"
                    data-test="production-overview.range-validation-message"
                >
                    {translate(`production-overview:${rangeValidationError}`)}
                </Typography>
            ) : undefined}
        </Box>
    );
};

export default OverviewFilterBar;
